import { UsageDetails } from '../../../types/subscription';

/**
 * Calculate the highest usage percentage across credits and syncs
 * Uses backend-calculated percentages for consistency
 */
export const calculateHighestUsage = (usage: UsageDetails): number => {
  const creditsPercentage = usage.credits.percentage || 0;
  const syncsPercentage = usage.syncs.percentage || 0;
  return Math.max(creditsPercentage, syncsPercentage);
};

/**
 * Format usage percentage with proper rounding
 */
export const formatUsagePercentage = (current: number, limit: number): string => {
  if (limit === 0) return '0%';
  return Math.round((current / limit) * 100) + '%';
};

/**
 * Format usage count with thousands separator
 */
export const formatUsageCount = (count: number): string => {
  return count.toLocaleString();
};

/**
 * Get usage status color based on percentage
 */
export const getUsageStatusColor = (percentage: number): string => {
  if (percentage >= 90) return 'text-red-600';
  if (percentage >= 75) return 'text-orange-600';
  if (percentage >= 50) return 'text-yellow-600';
  return 'text-green-600';
};

/**
 * Determine if usage is approaching limits (80%+)
 */
export const isApproachingLimit = (current: number, limit: number): boolean => {
  return (current / limit) >= 0.8;
};

/**
 * Determine if usage has reached limits (100%+)
 */
export const hasReachedLimit = (current: number, limit: number): boolean => {
  return current >= limit;
};

/**
 * Calculate remaining usage
 */
export const calculateRemaining = (current: number, limit: number): number => {
  return Math.max(0, limit - current);
};

/**
 * Get usage description for dynamic banner content
 * Now focused only on credit usage
 */
export const getUsageDescription = (
  usage: UsageDetails, 
  planName?: string
): string => {
  const creditsPercentage = usage.credits.percentage || 0;
  const planDisplay = planName || 'your plan';
  
  return `You've used ${creditsPercentage.toFixed(0)}% of your ${planDisplay} credits. Consider upgrading to avoid interruptions.`;
};

/**
 * Determine which usage metric is highest
 * Uses backend-calculated percentages for consistency
 */
export const getHighestUsageMetric = (usage: UsageDetails): 'credits' | 'syncs' => {
  const creditsPercentage = usage.credits.percentage || 0;
  const syncsPercentage = usage.syncs.percentage || 0;
  
  return creditsPercentage >= syncsPercentage ? 'credits' : 'syncs';
};

/**
 * Calculate days remaining in billing period
 */
export const getDaysRemainingInBillingPeriod = (usage: UsageDetails): number => {
  const endDate = new Date(usage.billing_period.end);
  const today = new Date();
  const diffTime = endDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

/**
 * Get banner priority based on usage levels
 * Higher number = higher priority
 * Uses backend-calculated percentages for consistency
 */
export const getBannerPriority = (usage: UsageDetails): number => {
  const creditsPercentage = usage.credits.percentage || 0;
  const syncsPercentage = usage.syncs.percentage || 0;
  const highestUsage = Math.max(creditsPercentage, syncsPercentage);
  
  // Critical: 100%+ usage
  if (highestUsage >= 100) return 5;
  
  // High: 90%+ usage
  if (highestUsage >= 90) return 4;
  
  // Medium: 80%+ usage
  if (highestUsage >= 80) return 3;
  
  // Low: 50%+ usage
  if (highestUsage >= 50) return 2;
  
  // Minimal: <50% usage
  return 1;
};